import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

// @MUI components
import {
    Box, Typography, Paper, IconButton, Tooltip, TextField, Dialog,
    DialogTitle, DialogContent, DialogActions, Alert, Chip, Switch, FormControlLabel,
    CircularProgress, LinearProgress, Button
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
// import SendIcon from '@mui/icons-material/Send';
import SecurityIcon from '@mui/icons-material/Security';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import logger from '../../utils/logger';

// Memer components
// import { useTransaction } from '../context/TransactionContext';

interface SecureDataViewerProps {
    data: string;
    title?: string;
    description?: string;
    onCopy?: () => void;
    contracts?: { name: string; address: string; abi: any }[];
}

// WARNING: This is a BASIC obfuscation technique, NOT true cryptographic security!
// DO NOT rely on this for protecting sensitive information
function simpleEncrypt(text: string, key: string): string {
    // Create a simple XOR cipher with the key
    const result = [];
    const keyChars = key.split('');

    for (let i = 0; i < text.length; i++) {
        const keyChar = keyChars[i % keyChars.length].charCodeAt(0);
        const textChar = text.charCodeAt(i);
        result.push(String.fromCharCode(textChar ^ keyChar));
    }

    return btoa(result.join('')); // Base64 encode the result
}

function simpleDecrypt(encrypted: string, key: string): string {
    try {
        // Current decryption logic
        const text = atob(encrypted);
        const result = [];
        const keyChars = key.split('');

        for (let i = 0; i < text.length; i++) {
            const keyChar = keyChars[i % keyChars.length].charCodeAt(0);
            const encChar = text.charCodeAt(i);
            result.push(String.fromCharCode(encChar ^ keyChar));
        }

        // Return without logging
        return result.join('');
    } catch (e) {
        // Log only the error, not the data
        logger.error("Decryption error occurred");
        return "Error decrypting data";
    }
}

export default function SecureDataViewer({ data, title = "Secure Data", description, onCopy, contracts = [] }: SecureDataViewerProps) {
    const [analysis, setAnalysis] = useState<any>(null);
    const [analysisError, setAnalysisError] = useState<string | null>(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [encryptedStorage, setEncryptedStorage] = useState(true);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [password, setPassword] = useState("");
    const [showAnalysis, setShowAnalysis] = useState(false);
    const [visible, setVisible] = useState(false);

    // Store data in localStorage with optional encryption
    useEffect(() => {
        if (!data) {
            setLoadingData(false);
            return;
        }

        try {
            // Local storage key derived from data content
            const storageKey = `secure-data-${ethers.utils.id(data).substring(0, 10)}`;

            if (encryptedStorage) {
                // Use a combination of window properties as the encryption key
                const browserKey = `${navigator.userAgent}-${window.location.host}`;
                const encrypted = simpleEncrypt(data, browserKey);
                localStorage.setItem(storageKey, encrypted);
            } else {
                // Store without encryption
                localStorage.setItem(storageKey, data);
            }
        } catch (e) {
            logger.error("Error storing data:", e);
        }

        // Simulate data loading
        const timer = setTimeout(() => {
            setLoadingData(false);
        }, 500);
        return () => clearTimeout(timer);
    }, [data, encryptedStorage]);

    useEffect(() => {
        // Example of using simpleDecrypt
        if (encryptedStorage) {
            const browserKey = `${navigator.userAgent}-${window.location.host}`;
            try {
                const decryptedData = simpleDecrypt(
                    localStorage.getItem(`secure-data-${ethers.utils.id(data).substring(0, 10)}`) || '',
                    browserKey
                );
                // logger.log('Decrypted data:', decryptedData);
            } catch (error) {
                logger.error('Decryption failed:', error);
            }
        }
    }, [data, encryptedStorage]);

    // const { setEncodedFunctionData } = useTransaction();

    // Enhanced contract-specific decoding for MEMER token functions
    const decodeMemerTokenFunction = (data: string, contracts: any[]) => {
        const memerCoreContract = contracts.find(c => c.name === 'MemerCore');
        if (!memerCoreContract) return null;

        const contractInterface = new ethers.utils.Interface(memerCoreContract.abi);

        try {
            // Check transfer functions
            const transferSignatures = [
                'transfer(address,uint256)',
                'transferFrom(address,address,uint256)'
            ];

            for (const signature of transferSignatures) {
                try {
                    const decoded = contractInterface.decodeFunctionData(signature, data);
                    return {
                        contract: 'MemerCore',
                        method: signature.split('(')[0],
                        arguments: {
                            ...(signature.includes('transferFrom')
                                ? {
                                    from: decoded[0],
                                    to: decoded[1],
                                    amount: ethers.utils.formatUnits(decoded[2], 18)
                                }
                                : {
                                    to: decoded[0],
                                    amount: ethers.utils.formatUnits(decoded[1], 18)
                                })
                        }
                    };
                } catch { }
            }
        } catch (error) {
            logger.warn("Error decoding Memer token function:", error);
        }
        return null;
    };

    const renderAddressWithTooltip = (address: string) => (
        <Box>
            <Tooltip title={address}>
                <Typography
                    variant="body2"
                    component="span"
                    sx={{ cursor: 'help', display: 'inline-block', fontFamily: 'monospace' }}
                >
                    {`${address.substring(0, 6)}...${address.substring(address.length - 4)}`}
                </Typography>
            </Tooltip>
        </Box>
    );

    // Address rendering with tooltip
    const renderAddressArgs = (args: any) => {
        return Object.entries(args).map(([key, value]) => {
            // Check if the value looks like an Ethereum address
            if (typeof value === 'string' && /^0x[a-fA-F0-9]{40}$/.test(value)) {
                return (
                    <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="body2" component="span" color="text.secondary">{key}:</Typography>
                        {renderAddressWithTooltip(value as string)}
                    </Box>
                );
            }
            return null;
        }).filter(Boolean);
    };

    // Function to analyze the data and identify what contract calls it contains
    const analyzeData = async () => {
        if (!data || contracts.length === 0) return;

        setAnalyzing(true);

        try {
            // First, try Memer-specific decoding
            const memerDecoding = decodeMemerTokenFunction(data, contracts);
            if (memerDecoding) {
                setAnalysis(memerDecoding);
                return;
            }

            // Existing decoding interface
            interface DecodedInfo {
                contract: string;
                method: string;
                args: any;
                signature: string;
                hexData: string;
                nested?: DecodedInfo | null;
            }

            let decodedInfo: DecodedInfo | null = null;

            // Try decoding with general contract ABIs
            for (const contract of contracts) {
                try {
                    const contractInterface = new ethers.utils.Interface(contract.abi);
                    const decoded = contractInterface.parseTransaction({ data });

                    if (decoded) {
                        decodedInfo = {
                            contract: contract.name,
                            method: decoded.name,
                            args: decoded.args,
                            signature: decoded.signature,
                            hexData: data
                        };
                        break;
                    }
                } catch (error) {
                    // This contract's ABI doesn't match the function signature, try the next one
                    continue;
                }
            }

            // Nested call analysis (existing logic remains the same)
            if (decodedInfo && decodedInfo.args) {
                const potentialDataParams = ['data', 'calldata', 'payload', '_data'];

                for (const paramName of potentialDataParams) {
                    if (decodedInfo.args[paramName] && typeof decodedInfo.args[paramName] === 'string' && decodedInfo.args[paramName].startsWith('0x')) {
                        const nestedData = decodedInfo.args[paramName];

                        // First, try Memer-specific nested decoding
                        const memerNestedDecoding = decodeMemerTokenFunction(nestedData, contracts);
                        if (memerNestedDecoding) {
                            decodedInfo.nested = {
                                ...memerNestedDecoding,
                                args: memerNestedDecoding.arguments,
                                signature: '',
                                hexData: nestedData
                            } as DecodedInfo;
                            break;
                        }

                        // Fall back to existing nested decoding logic
                        let nestedInfo = null;
                        for (const contract of contracts) {
                            try {
                                const contractInterface = new ethers.utils.Interface(contract.abi);
                                const decoded = contractInterface.parseTransaction({ data: nestedData });

                                if (decoded) {
                                    nestedInfo = {
                                        contract: contract.name,
                                        method: decoded.name,
                                        args: decoded.args,
                                        signature: decoded.signature,
                                        hexData: nestedData
                                    };
                                    break;
                                }
                            } catch (error) {
                                continue;
                            }
                        }

                        if (nestedInfo) {
                            decodedInfo.nested = nestedInfo;

                            // Third-level nesting check
                            if (nestedInfo.args) {
                                for (const paramName of potentialDataParams) {
                                    if (nestedInfo.args[paramName] && typeof nestedInfo.args[paramName] === 'string' && nestedInfo.args[paramName].startsWith('0x')) {
                                        const thirdLevelData = nestedInfo.args[paramName];

                                        // First, try Memer-specific third-level decoding
                                        const memerThirdLevelDecoding = decodeMemerTokenFunction(thirdLevelData, contracts);
                                        if (memerThirdLevelDecoding) {
                                            decodedInfo.nested = {
                                                contract: memerThirdLevelDecoding.contract,
                                                method: memerThirdLevelDecoding.method,
                                                args: memerThirdLevelDecoding.arguments,
                                                signature: '',
                                                hexData: thirdLevelData
                                            };
                                            break;
                                        }

                                        // Fall back to existing third-level decoding
                                        let thirdLevelInfo = null;
                                        for (const contract of contracts) {
                                            try {
                                                const contractInterface = new ethers.utils.Interface(contract.abi);
                                                const decoded = contractInterface.parseTransaction({ data: thirdLevelData });

                                                if (decoded) {
                                                    thirdLevelInfo = {
                                                        contract: contract.name,
                                                        method: decoded.name,
                                                        args: decoded.args,
                                                        signature: decoded.signature,
                                                        hexData: thirdLevelData
                                                    };
                                                    break;
                                                }
                                            } catch (error) {
                                                continue;
                                            }
                                        }

                                        if (thirdLevelInfo) {
                                            decodedInfo.nested = thirdLevelInfo;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            setAnalysis(decodedInfo);
        } catch (error) {
            logger.error("Error analyzing data:", error);
            setAnalysis({ error: "Failed to decode transaction data" });
        } finally {
            setAnalyzing(false);
        }
    };

    async function handleAnalyzeClick() {
        setIsAnalyzing(true);
        setAnalysisError(null);
        try {
            await analyzeData();
            setShowAnalysis(true);
        } catch (error) {
            setAnalysisError(error instanceof Error ? error.message : 'Analysis failed');
        } finally {
            setIsAnalyzing(false);
        }
    }

    // Helper to render a truncated version of the data
    const renderTruncatedData = () => {
        if (!data) return "No data";

        const prefix = data.substring(0, 10);
        const suffix = data.substring(data.length - 8);

        return (
            <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
                {prefix}...{suffix}
            </Typography>
        );
    };

    // Helper to render function arguments in a readable format
    // Helper to render function arguments in a readable format
    const renderArgs = (args: any) => {
        return Object.entries(args).map(([key, value]) => {
            // Skip numeric keys (array indexes)
            if (!isNaN(Number(key))) return null;

            // Format the value based on its type
            let formattedValue: any = value;

            if (typeof value === 'string' && value.startsWith('0x')) {
                // Address or hex data
                if (value.length === 42) {
                    // Likely an address
                    formattedValue = renderAddressWithTooltip(value);
                } else if (value.length > 66) {
                    // Likely hex data
                    formattedValue = (
                        <Tooltip title={value}>
                            <Typography>
                                {value.substring(0, 10)}...{value.substring(value.length - 8)}
                            </Typography>
                        </Tooltip>
                    );
                }
            } else if (ethers.BigNumber.isBigNumber(value)) {
                // Format BigNumber for display
                // Try to guess if it's a token amount (multiply by 10^18)
                const num = ethers.BigNumber.from(value);
                if (num.gt(ethers.BigNumber.from(10).pow(15)) && num.lt(ethers.BigNumber.from(10).pow(30))) {
                    formattedValue = `${ethers.utils.formatEther(num)} ETH`;
                } else {
                    formattedValue = num.toString();
                }
            }

            return (
                <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="body2" component="span" color="text.secondary">{key}:</Typography>
                    <Typography variant="body2" component="span">{formattedValue}</Typography>
                </Box>
            );
        }).filter(Boolean);
    };

    const copyToClipboard = () => {
        if (!data) return;

        navigator.clipboard.writeText(data)
            .then(() => {
                if (onCopy) onCopy();
            })
            .catch(err => {
                logger.error('Failed to copy: ', err);
            });
    };

    // Function to reveal the data after password check
    const revealData = () => {
        // Simple password check - in a real application, use a more secure approach
        if (password.length >= 4) {
            setVisible(true);
            setDialogOpen(false);
        }
    };

    // Enhanced error display component
    const ErrorDisplay = ({ message }: { message: string }) => (
        <Alert
            severity="error"
            icon={<ErrorOutlineIcon />}
            action={
                <Button color="inherit" size="small" onClick={() => setAnalysisError(null)}>
                    DISMISS
                </Button>
            }
        >
            {message}
        </Alert>
    );

    return (
        <Paper
            elevation={3}
            sx={{
                p: 2,
                position: 'relative',
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                mt: 2,
                background: theme => theme.palette.background.paper,
                transition: 'all 0.3s ease'
            }}
        >
            {/* Warning banner about encryption */}
            <Alert
                severity="warning"
                sx={{ mb: 2 }}
            >
                Local storage encryption is a basic obfuscation technique
                and provides NO real cryptographic security. Do NOT store
                sensitive information this way.
            </Alert>

            {/* Loading overlay */}
            {loadingData && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <CircularProgress size={40} />
                    <Typography sx={{ mt: 2 }}>Loading secure data...</Typography>
                </Box>
            )}

            {/* Header with title and action buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SecurityIcon color="primary" />
                    {title}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        disabled={isAnalyzing}
                        onClick={handleAnalyzeClick}
                        variant="outlined"
                        size="small"
                        startIcon={isAnalyzing ? <CircularProgress size={16} /> : <SecurityIcon />}
                    >
                        {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                    </Button>

                    <Tooltip title={visible ? "Hide data" : "Show data"}>
                        <IconButton
                            size="small"
                            onClick={() => visible ? setVisible(false) : setDialogOpen(true)}
                        >
                            {visible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Copy to clipboard">
                        <IconButton size="small" onClick={copyToClipboard}>
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Description */}
            {description && (
                <Typography
                    variant="body2"
                    component="span"
                    color="text.secondary"
                    sx={{ mb: 2, fontStyle: 'italic' }}
                >
                    {description}
                </Typography>
            )}

            {/* Error display */}
            {analysisError && <ErrorDisplay message={analysisError} />}

            {/* Progressive loading for analysis */}
            {isAnalyzing && (
                <Box sx={{ width: '100%', mb: 2 }}>
                    <LinearProgress />
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                        Analyzing transaction data...
                    </Typography>
                </Box>
            )}

            {/* Main data display section */}
            <Box
                sx={{
                    bgcolor: 'action.hover',
                    p: 1.5,
                    borderRadius: 1,
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {visible ? (
                    <Box sx={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {data}
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {renderTruncatedData()}
                        <Button
                            startIcon={<LockOpenIcon />}
                            size="small"
                            variant="outlined"
                            onClick={() => setDialogOpen(true)}
                        >
                            Unlock
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Analysis results section */}
            {showAnalysis && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Data Analysis
                    </Typography>

                    {analyzing ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : analysis ? (
                        <Box>
                            {analysis.error ? (
                                <Alert severity="warning">{analysis.error}</Alert>
                            ) : (
                                <Box>
                                    {/* Contract Info */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="body2" fontWeight="bold" mr={1}>
                                            Contract:
                                        </Typography>
                                        <Chip size="small" label={analysis.contract} color="primary" />
                                    </Box>

                                    {/* Function Info */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="body2" fontWeight="bold" mr={1}>
                                            Function:
                                        </Typography>
                                        <Chip size="small" label={analysis.method} color="secondary" />
                                    </Box>

                                    {/* Arguments */}
                                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                                        Arguments:
                                    </Typography>
                                    <Box sx={{ pl: 2 }}>
                                        {renderArgs(analysis.args)}
                                    </Box>

                                    {analysis && analysis.args && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2">Address Arguments:</Typography>
                                            {renderAddressArgs(analysis.args)}
                                        </Box>
                                    )}

                                    {/* Nested Call Analysis */}
                                    {analysis.nested && (
                                        <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                                            <Typography variant="body2" fontWeight="bold">
                                                Nested Call:
                                            </Typography>

                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
                                                <Typography variant="body2" fontWeight="bold" mr={1}>
                                                    Contract:
                                                </Typography>
                                                <Chip size="small" label={analysis.nested.contract} color="primary" />
                                            </Box>

                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="body2" fontWeight="bold" mr={1}>
                                                    Function:
                                                </Typography>
                                                <Chip size="small" label={analysis.nested.method} color="secondary" />
                                            </Box>

                                            <Typography variant="body2" fontWeight="bold" gutterBottom>
                                                Arguments:
                                            </Typography>
                                            <Box sx={{ pl: 2 }}>
                                                {renderArgs(analysis.nested.args)}
                                            </Box>

                                            {/* Second-Level Nested Call */}
                                            {analysis.nested.nested && (
                                                <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        Second-Level Nested Call:
                                                    </Typography>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
                                                        <Typography variant="body2" fontWeight="bold" mr={1}>
                                                            Contract:
                                                        </Typography>
                                                        <Chip size="small" label={analysis.nested.nested.contract} color="primary" />
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="body2" fontWeight="bold" mr={1}>
                                                            Function:
                                                        </Typography>
                                                        <Chip size="small" label={analysis.nested.nested.method} color="secondary" />
                                                    </Box>

                                                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                                                        Arguments:
                                                    </Typography>
                                                    <Box sx={{ pl: 2 }}>
                                                        {renderArgs(analysis.nested.nested.args)}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Typography variant="body2" component="span" color="text.secondary">
                            No analysis data available
                        </Typography>
                    )}
                </Box>
            )}

            {/* Password Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LockIcon sx={{ mr: 1 }} />
                        Unlock Secure Data
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Typography variant="body2" component="span" sx={{ mb: 2 }}>
                        Please enter your password to view the sensitive data.
                    </Typography>

                    <TextField
                        autoFocus
                        label="Password"
                        type="password"
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && revealData()}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={encryptedStorage}
                                onChange={(e) => setEncryptedStorage(e.target.checked)}
                            />
                        }
                        label="Encrypt in local storage"
                        sx={{ mt: 2 }}
                    />

                    <Alert severity="info" sx={{ mt: 2 }}>
                        This is a basic security measure for demonstration. For real security, use proper authentication.
                    </Alert>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={revealData} variant="contained" disabled={password.length < 4}>
                        Unlock
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
